// src/features/marketResearch/components/sections/ExecutiveSummary.tsx
import React from 'react';
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Chip,
  Paper,
  Divider,
  useTheme,
  useMediaQuery,
  alpha
} from '@mui/material';
import {
  TrendingUp,
  Assessment,
  AccountBalance,
  Security,
  Recommend,
  Star,
  Timeline,
  Business,
  CheckCircle,
  Info,
  ShowChart,
  VerifiedUser
} from '@mui/icons-material';
import { SectionContainer, SectionTitle, SectionBody, SubSectionTitle } from '../Section';

interface Props {
  summary: string;
}

export const ExecutiveSummary: React.FC<Props> = ({ summary }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Extract key insights from summary or use defaults
  const extractInsights = (text: string) => {
    const hasPositiveIndicators = text.toLowerCase().includes('growth') ||
                                  text.toLowerCase().includes('strong') ||
                                  text.toLowerCase().includes('opportunity');

    const hasMarketSize = text.includes('$') || text.includes('million') || text.includes('billion');

    return {
      recommendation: hasPositiveIndicators ? 'BUY' : 'HOLD',
      riskLevel: 'MODERATE',
      confidence: hasMarketSize ? 'HIGH' : 'MEDIUM',
      outlook: hasPositiveIndicators ? 'POSITIVE' : 'NEUTRAL'
    };
  };

  const insights = extractInsights(summary);

  const keyMetrics = [
    {
      title: 'Recommendation',
      value: insights.recommendation,
      icon: <ShowChart />,
      color: insights.recommendation === 'BUY' ? theme.palette.success.main : theme.palette.warning.main,
    },
    {
      title: 'Risk Level',
      value: insights.riskLevel,
      icon: <VerifiedUser />,
      color: theme.palette.info.main,
    },
    {
      title: 'Outlook',
      value: insights.outlook,
      icon: <TrendingUp />,
      color: insights.outlook === 'POSITIVE' ? theme.palette.success.main : theme.palette.warning.main,
    },
    {
      title: 'Confidence',
      value: insights.confidence,
      icon: <Assessment />,
      color: theme.palette.primary.main,
    }
  ];

  const strategicHighlights = [
    'Prime location with sustainable competitive advantages',
    'Strong market fundamentals supporting revenue growth',
    'Identified operational efficiency opportunities',
    'Diversified demand base reducing market risk'
  ];

  return (
    <SectionContainer id="executive-summary">
      <SectionTitle>Executive Summary</SectionTitle>
      <SectionBody>
        {/* Main Content Card */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          {/* Compact Metrics Row */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mb: 4,
              pb: 3,
              borderBottom: `1px solid ${theme.palette.divider}`,
              flexWrap: 'wrap',
              justifyContent: 'space-between'
            }}
          >
            {keyMetrics.map((metric, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flex: { xs: '1 1 45%', sm: '1 1 auto' }
                }}
              >
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    backgroundColor: alpha(metric.color, 0.08),
                    color: metric.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {React.cloneElement(metric.icon, { sx: { fontSize: 20 } })}
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block'
                    }}
                  >
                    {metric.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: metric.color,
                      fontSize: '0.875rem'
                    }}
                  >
                    {metric.value}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Investment Summary */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 2,
                fontSize: '1.1rem',
                color: theme.palette.text.primary
              }}
            >
              Investment Overview
            </Typography>
            <Typography
              variant="body2"
              sx={{
                lineHeight: 1.7,
                color: theme.palette.text.secondary,
                fontSize: '0.875rem'
              }}
            >
              {summary || 'This market research analysis provides a comprehensive evaluation of the hotel investment opportunity, examining market fundamentals, competitive positioning, and growth prospects. The analysis indicates a well-positioned asset in a stable market environment with multiple value creation opportunities.'}
            </Typography>
          </Box>

          {/* Strategic Highlights - Compact List */}
          <Box
            sx={{
              p: 2.5,
              backgroundColor: alpha(theme.palette.primary.main, 0.03),
              borderRadius: 1.5,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: theme.palette.text.primary,
                fontSize: '0.875rem'
              }}
            >
              Key Investment Highlights
            </Typography>
            <Grid container spacing={1.5}>
              {strategicHighlights.map((highlight, idx) => (
                <Grid item xs={12} md={6} key={idx}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <CheckCircle
                      sx={{
                        fontSize: 16,
                        color: theme.palette.primary.main,
                        mr: 1,
                        mt: 0.2,
                        flexShrink: 0
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.8rem',
                        lineHeight: 1.5,
                        color: theme.palette.text.secondary
                      }}
                    >
                      {highlight}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Bottom Line - Simplified */}
          <Box
            sx={{
              mt: 4,
              p: 2,
              backgroundColor: alpha(theme.palette.background.default, 0.5),
              borderRadius: 1,
              textAlign: 'center'
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '0.8rem',
                lineHeight: 1.6
              }}
            >
              <Box component="span" sx={{ fontWeight: 600 }}>Bottom Line:</Box> This investment presents a{' '}
              <Box
                component="span"
                sx={{
                  fontWeight: 600,
                  color: insights.recommendation === 'BUY' ? theme.palette.success.main : theme.palette.warning.main
                }}
              >
                {insights.recommendation === 'BUY' ? 'compelling' : 'solid'}
              </Box>
              {' '}opportunity with{' '}
              <Box
                component="span"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.info.main
                }}
              >
                {insights.riskLevel.toLowerCase()}
              </Box>
              {' '}risk and{' '}
              <Box
                component="span"
                sx={{
                  fontWeight: 600,
                  color: insights.outlook === 'POSITIVE' ? theme.palette.success.main : theme.palette.warning.main
                }}
              >
                {insights.outlook.toLowerCase()}
              </Box>
              {' '}market outlook.
            </Typography>
          </Box>
        </Paper>
      </SectionBody>
    </SectionContainer>
  );
};
